import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import Product from '@/models/Products/Product';
import { AppModule } from '@/store/Auth/AppModule';
import ProductType from '@/models/Products/ProductType';
import ProductSubType from '@/models/Products/ProductSubType';
import ProductState from '@/models/Products/ProductState';
import PermissionUtils from '@/lib/permissionUtils';

export interface IProductListState {
    loading: boolean;
    products: Product[];
    totalProductsPages: number;
    filterByProductType: ProductType;
    filterByProductSubType: ProductSubType;
    filterByProductState: ProductState;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'ProductListModule', namespaced: true })
class ProductListStore extends VuexModule implements IProductListState {

    public loading: boolean = true;
    public products: Product[] = [];
    public productsForDialog: Product[] = [];
    public totalProductsPages: number = 1;
    public filterByProductType: ProductType = null;
    public filterByProductSubType: ProductSubType = null;
    public filterByProductState: ProductState = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getProducts(pageNumber: number) {

        this.setLoading(true);

        const productTypeParameter = this.filterByProductType ? `&type=${this.filterByProductType}` : ``;
        const productSubTypeParameter = this.filterByProductSubType ? `&subType=${this.filterByProductSubType}` : ``;
        const stateParameter = this.filterByProductState ? `&state=${this.filterByProductState}` : ``;

        await ajax.get(`/portal-backend/fi-api/internal/products?pageSize=${this.pageSize}&pageNumber=${pageNumber}${productTypeParameter}${productSubTypeParameter}${stateParameter}`)
        .then(response => {
            this.context.commit("setProducts", response.data.results);
            this.context.commit("setProductsTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getProductsForDialog() {

        // This action is specifically for the edit/create dialogs where we need to ignore the normal API pagination
        // logic in getProducts and instead get all products so they can be listed in the drop down menu for dialogs
        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/products`)
        .then(response => {
            this.context.commit("setProductsForDialog", response.data.results);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    updateProductTypeFilter(productTypeFilter: ProductType) {
        this.context.commit("setFilterByProductType", productTypeFilter);
    }

    @Action
    updateProductSubTypeFilter(productSubTypeFilter: ProductSubType) {
        this.context.commit("setFilterByProductSubType", productSubTypeFilter);
    }

    @Action
    updateProductStateFilter(productStateFilter: ProductState) {
        this.context.commit("setFilterByProductState", productStateFilter);
    }

    @Action
    clearAllFilters() {
        this.context.commit("setFilterByProductType", null);
        this.context.commit("setFilterByProductSubType", null);
        this.context.commit("setFilterByProductState", null);
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.products = [];
        this.productsForDialog = [];
        this.totalProductsPages = 1;
        this.filterByProductType = null;
        this.filterByProductSubType = null;
        this.filterByProductState = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setProducts(response: Product[]) {
        if (response) {
            this.products = response;
        }
    }

    @Mutation
    private async setProductsForDialog(response: Product[]) {
        if (response) {
            this.productsForDialog = response;
        }
    }

    @Mutation
    private async setProductsTotalPages(total: number) {
        this.totalProductsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setFilterByProductType(filter: ProductType) {
        this.filterByProductType = filter;
    }

    @Mutation
    private async setFilterByProductSubType(filter: ProductSubType) {
        this.filterByProductSubType = filter;
    }

    @Mutation
    private async setFilterByProductState(filter: ProductState) {
        this.filterByProductState = filter;
    }

}

export const ProductListModule = getModule(ProductListStore);